import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SLOT_MINUTES, SystemRole, TRIP_TYPE_LABELS, TripType } from '@fleet/shared';
import { DayCalendar } from './DayCalendar';
import { WeekCalendar } from './WeekCalendar';
import { BookingDialog } from './BookingDialog';
import { CalendarFilters, type CalendarFilterState } from './CalendarFilters';
import { RescheduleDialog } from '../bookings/RescheduleDialog';
import {
  DAY_START_HOUR,
  formatDay,
  formatDayMonth,
  formatTime,
  isSameDay,
  startOfWeek,
  weekDays,
} from './calendar-geometry';
import { useAuth } from '../auth/useAuth';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ErrorNotice } from '../ui/ErrorNotice';
import { bookingsApi, type CalendarEntry } from '../api/bookings';

type View = 'day' | 'week';

/** Vorbelegung des Buchungsformulars — leer, wenn über den Knopf geöffnet. */
type BookingDraft = { vehicleId?: string; from?: Date; to?: Date };

/**
 * Kalender mit Tages- und Wochenansicht.
 *
 * Datum und Filter liegen hier statt in den beiden Ansichten: Wer von der Woche
 * in den Tag wechselt, will denselben Ausschnitt weitersehen, nicht wieder bei
 * heute und ungefiltert anfangen.
 */
export function CalendarPage() {
  // Die Woche als Einstieg: Sie beantwortet die Frage, die zuerst gestellt wird
  // („wann ist überhaupt etwas frei"). Der Tag ist der zweite Schritt.
  const [view, setView] = useState<View>('week');
  const [anchor, setAnchor] = useState(() => new Date());
  const [filters, setFilters] = useState<CalendarFilterState>({
    vehicleGroupId: '',
    vehicleIds: new Set(),
  });

  const [draft, setDraft] = useState<BookingDraft | null>(null);
  const [openEntry, setOpenEntry] = useState<CalendarEntry | null>(null);

  const shift = (amount: number) => {
    const next = new Date(anchor);
    next.setDate(next.getDate() + amount * (view === 'week' ? 7 : 1));
    setAnchor(next);
  };

  const isCurrent =
    view === 'day'
      ? isSameDay(anchor, new Date())
      : startOfWeek(anchor).getTime() === startOfWeek(new Date()).getTime();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => shift(-1)} aria-label="Zurück">
            ‹
          </Button>
          <Button
            size="sm"
            variant={isCurrent ? 'primary' : 'secondary'}
            onClick={() => setAnchor(new Date())}
          >
            Heute
          </Button>
          <Button size="sm" onClick={() => shift(1)} aria-label="Weiter">
            ›
          </Button>
          <span className="ml-1 text-sm font-medium">{rangeLabel(view, anchor)}</span>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex gap-1 rounded-md bg-slate-100 p-1">
            <ViewButton active={view === 'day'} onClick={() => setView('day')}>
              Tag
            </ViewButton>
            <ViewButton active={view === 'week'} onClick={() => setView('week')}>
              Woche
            </ViewButton>
          </div>

          {/*
            Der Weg ohne Kalenderklicks. In der Wochenansicht ist er der
            einzige — dort ließe sich eine Viertelstunde nicht auflösen —, in
            der Tagesansicht die Alternative zum Ziehen.
          */}
          <Button
            variant="primary"
            size="sm"
            onClick={() =>
              setDraft({
                // Der betrachtete Tag ist die wahrscheinlichere Absicht als heute.
                from: startOfSlotOn(anchor),
              })
            }
          >
            Fahrzeug buchen
          </Button>
        </div>
      </div>

      <CalendarFilters value={filters} onChange={setFilters} />

      {view === 'day' ? (
        <DayCalendar
          day={anchor}
          vehicleGroupId={filters.vehicleGroupId || undefined}
          vehicleIds={filters.vehicleIds}
          onSelectRange={(vehicle, from, to) => setDraft({ vehicleId: vehicle.id, from, to })}
          onSelectBooking={setOpenEntry}
        />
      ) : (
        <WeekCalendar
          week={anchor}
          vehicleGroupId={filters.vehicleGroupId || undefined}
          vehicleIds={filters.vehicleIds}
          onSelectBooking={setOpenEntry}
          onSelectDay={(day) => {
            setAnchor(day);
            setView('day');
          }}
        />
      )}

      {draft && <BookingDialog initial={draft} onClose={() => setDraft(null)} />}

      {openEntry && <EntryDetails entry={openEntry} onClose={() => setOpenEntry(null)} />}
    </div>
  );
}

/**
 * Startzeit für eine Buchung am betrachteten Tag.
 *
 * Für heute die nächste Viertelstunde, für jeden anderen Tag der Beginn der
 * Anzeigezeit — eine Buchung, die auf „jetzt" an einem vergangenen Vormittag
 * vorbelegt wäre, müsste man in jedem Fall korrigieren.
 */
function startOfSlotOn(day: Date): Date {
  const now = new Date();
  if (isSameDay(day, now)) {
    const next = new Date(now);
    next.setSeconds(0, 0);
    next.setMinutes(Math.ceil(next.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES);
    return next;
  }

  const result = new Date(day);
  result.setHours(DAY_START_HOUR + 2, 0, 0, 0);
  return result;
}

function rangeLabel(view: View, anchor: Date): string {
  if (view === 'day') return formatDay(anchor);

  const days = weekDays(anchor);
  return `${formatDayMonth(days[0]!)} – ${formatDay(days[6]!)}`;
}

function ViewButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        'rounded px-3 py-1 text-sm transition-colors',
        active ? 'bg-white shadow-sm' : 'text-slate-600 hover:text-slate-900',
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function EntryDetails({ entry, onClose }: { entry: CalendarEntry; onClose: () => void }) {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const isAdmin = auth.hasRole(SystemRole.ADMIN);
  const [rescheduling, setRescheduling] = useState(false);

  const start = new Date(entry.startsAt);
  const end = new Date(entry.endsAt);
  const spansDays = !isSameDay(start, end);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['availability'] });
    await queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
    await queryClient.invalidateQueries({ queryKey: ['pending-approvals'] });
    await queryClient.invalidateQueries({ queryKey: ['all-bookings'] });
    onClose();
  };

  const cancel = useMutation({
    mutationFn: () => bookingsApi.cancel(entry.id),
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: () => bookingsApi.remove(entry.id),
    onSuccess: invalidate,
  });

  return (
    <Modal title="Belegung" onClose={onClose} size="sm">
      {/*
        Zeiten ausgeschrieben statt nur als Block im Raster: Aus der Höhe eines
        Balkens die Uhrzeit abzulesen ist Schätzarbeit, und bei einer Buchung,
        die man gleich stornieren will, ist Schätzen die falsche Grundlage.
      */}
      <div className="rounded-md bg-slate-50 px-3 py-2.5">
        <div className="text-lg font-medium tabular-nums">
          {formatTime(start)} – {formatTime(end)}
        </div>
        <div className="mt-0.5 text-sm text-slate-500">
          {spansDays ? `${formatDay(start)} bis ${formatDay(end)}` : formatDay(start)}
        </div>
        <div className="mt-1 text-xs text-slate-500">Dauer: {formatDuration(start, end)}</div>
      </div>

      <dl className="mt-4 space-y-2 text-sm">
        <Detail label="Buchung" value={entry.bookingNumber} />
        <Detail label="Fahrer" value={entry.driverName ?? 'nicht sichtbar'} />
        <Detail label="Fahrtart" value={TRIP_TYPE_LABELS[entry.tripType]} />
        {entry.publicPurpose && <Detail label="Anlass" value={entry.publicPurpose} />}
        {/*
          Der volle Zweck nur, wenn er auch mitgeliefert wurde — und nur, wenn
          er etwas anderes sagt als der Kalendertext. Zweimal dieselbe Zeile
          untereinander sähe nach einem Fehler aus.
        */}
        {entry.purpose && entry.purpose !== entry.publicPurpose && (
          <Detail label="Zweck" value={entry.purpose} />
        )}
      </dl>

      {entry.tripType === TripType.PRIVAT && (
        <p className="mt-4 text-xs leading-relaxed text-slate-500">
          Bei Privatfahrten werden Zweck und Ziel nicht erfasst.
        </p>
      )}

      {(cancel.isError || remove.isError) && (
        <div className="mt-4">
          <ErrorNotice error={cancel.error ?? remove.error} />
        </div>
      )}

      <div className="mt-6 space-y-2">
        {entry.canManage && (
          <Button variant="secondary" className="w-full" onClick={() => setRescheduling(true)}>
            Zeiten ändern
          </Button>
        )}

        {entry.canManage && (
          <Button
            variant="secondary"
            className="w-full"
            disabled={cancel.isPending}
            onClick={() => {
              if (
                confirm(
                  `${entry.bookingNumber} stornieren?\n\nDer Zeitraum wird dadurch sofort wieder frei.`,
                )
              ) {
                cancel.mutate();
              }
            }}
          >
            {cancel.isPending ? 'Wird storniert …' : 'Stornieren'}
          </Button>
        )}

        {isAdmin && (
          <Button
            variant="danger"
            className="w-full"
            disabled={remove.isPending}
            onClick={() => {
              if (
                confirm(
                  `${entry.bookingNumber} endgültig löschen?\n\nStornieren gibt den Zeitraum ebenso frei und lässt nachvollziehbar, dass es die Buchung gab. Löschen ist für Fehleingaben und Testdaten gedacht.`,
                )
              ) {
                remove.mutate();
              }
            }}
          >
            {remove.isPending ? 'Wird gelöscht …' : 'Endgültig löschen'}
          </Button>
        )}

        <Button variant="ghost" className="w-full" onClick={onClose}>
          Schließen
        </Button>
      </div>

      {rescheduling && (
        <RescheduleDialog
          booking={entry}
          onClose={() => {
            setRescheduling(false);
            onClose();
          }}
        />
      )}
    </Modal>
  );
}

function formatDuration(start: Date, end: Date): string {
  const minutes = Math.round((end.getTime() - start.getTime()) / 60_000);
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;

  if (hours === 0) return `${rest} Minuten`;
  if (rest === 0) return hours === 1 ? '1 Stunde' : `${hours} Stunden`;
  return `${hours} h ${String(rest).padStart(2, '0')} min`;
}

function Detail({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}
